const API_BASE = import.meta.env.VITE_API_BASE || 'https://anika-agent.tnvmac.workers.dev';

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const apiClient = {
  // Health
  health: () => api<{ status: string }>('/health'),

  // Sessions
  listSessions: (params?: { limit?: number; offset?: number; source?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    if (params?.source) searchParams.set('source', params.source);
    return api<{ sessions: import('@/types').Session[]; total: number }>(`/api/sessions?${searchParams}`);
  },

  getSession: (id: string) =>
    api<import('@/types').Session>(`/api/sessions/${id}`),

  getMessages: (sessionId: string, params?: { limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());
    return api<{ messages: import('@/types').Message[]; total: number }>(`/api/sessions/${sessionId}/messages?${searchParams}`);
  },

  searchSessions: (query: string, limit = 20) =>
    api<{ results: import('@/types').Message[] }>(`/api/sessions/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  getMemory: (sessionId: string) =>
    api<{ memory: import('@/types').MemoryChunk[] }>(`/api/sessions/${sessionId}/memory`),

  // Agent
  chat: (sessionId: string, message: string, options?: { images?: string[] }) =>
    api<import('@/types').ChatResponse>(`/api/agent/chat`, {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId, message, ...options }),
    }),

  createSession: (platform: string, chatId: string, userId?: string) =>
    api<{ session_id: string }>('/api/gateway/session', {
      method: 'POST',
      body: JSON.stringify({ platform, chat_id: chatId, user_id: userId }),
    }),

  // Skills
  listSkills: (category?: string) =>
    api<{ skills: import('@/types').Skill[] }>(`/api/skills${category ? `?category=${category}` : ''}`),

  getSkill: (name: string) =>
    api<import('@/types').Skill>(`/api/skills/${name}`),

  // MCP
  mcpInfo: () =>
    api<import('@/types').McpInfo>(`/api/mcp/info`),

  mcpCall: (tool: string, args: Record<string, unknown>) =>
    api<import('@/types').McpCallResponse>(`/mcp/tools/call`, {
      method: 'POST',
      body: JSON.stringify({ tool, arguments: args }),
    }),
};